import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';

export const Route = createFileRoute('/camera-permission')({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const checkCameraPermission = async () => {
    try {
      // Check current permission state
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'camera' as PermissionName });

        if (permission.state === 'denied') {
          return;
        }

        if (permission.state === 'granted') {
          navigate({ to: '/skincheck' });
          return;
        }

        if (permission.state === 'prompt') {
          navigate({ to: '/' });
          return;
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error('Permission check failed:', error);
    }
  };
  useEffect(() => {
    checkCameraPermission();
  }, []);

  return (
    <div className="flex h-full w-screen items-center justify-center gap-8 bg-[url(src/assets/images/login/login-background.jpg)] bg-cover bg-no-repeat py-12">
      <div className="flex w-[calc(100vw-32px)] flex-col rounded-[1.5rem] bg-white p-8 shadow-[0px_12px_24px_0px_#0000001F] sm:w-[800px]">
        <div className="mx-auto mb-8">
          <img src="/images/camera-slash.png" alt="" style={{ width: '80px', height: '80px' }} />
        </div>
        <div className="relative mb-4 flex flex-row items-center justify-center">
          <span className="flex-1 text-center text-xl leading-[120%] text-[#171315] sm:text-[28px]">
            Camera Access Required
          </span>
        </div>
        <p className="font-bold">Looks like your camera permission is currently disabled.</p>
        <p>To continue, we need access to your camera so we can serve you better.</p>
        <div className="mt-8 mb-2">
          <h2 className="mb-2 text-[30px] font-semibold">Why do we need access to your camera?</h2>
          <p className="mb-2">
            We ask for access to your camera so you can take a quick selfie. Here's why this is
            important:
          </p>
          <ul className="list-inside list-disc space-y-2">
            <li>
              <span className="font-semibold">Your selfie powers our image-based AI</span>
              <p className="mt-2 ml-[22px]">
                With your permission, we use your selfie to
                <span className="font-semibold"> analyze your skin type</span> — such as oily, dry,
                normal, or resistance.
              </p>
            </li>
            <li>
              <span className="font-semibold">Better skincare recommendations</span>
              <p className="mt-2 ml-[22px]">
                Once we understand your skin type, we can offer
                <span className="font-semibold">
                  {' '}
                  personalized product and routine suggestions{' '}
                </span>
                tailored to your unique needs.
              </p>
            </li>
          </ul>
          <p className="mt-4">
            Please refer to our{' '}
            <a href="#" className="text-gray-400 underline">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="#" className="text-gray-400 underline">
              Terms &amp; Conditions
            </a>{' '}
            for full term of services.
          </p>
        </div>
        <div className="h-[1px] w-full bg-gray-300"></div>
        <div className="mt-8 mb-2">
          <h2 className="mb-2 text-[30px] font-semibold">How to reset camera permissions</h2>
          <p className="mb-2">
            If you previously denied camera access and want to enable it again, choose your browser
            below for official instructions:
          </p>
          <ul className="list-inside list-disc space-y-2">
            <li>
              <a
                href="https://support.google.com/chrome/answer/2693767"
                className="font-semibold text-gray-400 underline"
              >
                Google Chrome
              </a>
              <p className="mt-2 ml-[22px]">Manage site permissions including camera access.</p>
            </li>
            <li>
              <a
                href="https://support.apple.com/en-us/guide/mac-help/mchlf6d108da/mac"
                className="font-semibold text-gray-400 underline"
              >
                Safari (macOS)
              </a>
              <p className="mt-2 ml-[22px]">Change camera settings for specific websites.</p>
            </li>
            <li>
              <a
                href="https://support.mozilla.org/en-US/kb/how-manage-your-camera-and-microphone-permissions"
                className="font-semibold text-gray-400 underline"
              >
                Firefox
              </a>
              <p className="mt-2 ml-[22px]">Allow or block camera use on a per-site basis.</p>
            </li>
            <li>
              <a
                href="https://support.microsoft.com/en-us/windows/windows-camera-microphone-and-privacy-a83257bc-e990-d54a-d212-b5e41beba857"
                className="font-semibold text-gray-400 underline"
              >
                Microsoft Edge
              </a>
              <p className="mt-2 ml-[22px]">Update your privacy settings to allow camera access.</p>
            </li>
          </ul>
        </div>
        <div className="h-[1px] w-full bg-gray-300"></div>
        <div className="mt-8 mb-2">
          <h2 className="mb-2 text-[30px] font-semibold">What's next</h2>
          <p className="mb-2">
            Once you've reset your camera permission, your browser may prompt you to{' '}
            <span className="font-semibold">reload the page</span>.
          </p>
          <p className="mb-2">
            Feel free to do so — and then enjoy the full experience we've designed for your skin
            health journey!
          </p>
          <p className="mb-2 font-semibold">Have questions?</p>
          <p>
            Reach out to us anytime at{' '}
            <a href="mailto:hello@acie.skin" className="text-gray-400 underline">
              hello@acie.skin
            </a>{' '}
            — we're happy to help.
          </p>
        </div>
      </div>
    </div>
  );
}
